/* The scheduler: what an answer is worth, when a word comes back,
 * and which words a game round is built from.
 *
 * MEMORY MODEL: this is a modified SM-2, not FSRS.
 * Three deliberate departures from textbook SM-2:
 *   1. Answers carry a weight (GAMES[].w) so a 1-in-2 guess moves a word far
 *      less than typing it from nothing. Classic SM-2 treats every grade the
 *      same regardless of how much the question actually proved.
 *   2. A learning phase in front of the review schedule (1/9/25 minutes), as
 *      Anki does, so a new word can't jump to a day-scale interval on one
 *      lucky answer.
 *   3. Four independent skill scores per word rather than one grade, which is
 *      what drives the stage pipeline.
 * The ease/interval arithmetic underneath is SM-2's: interval *= ease, ease
 * moves by ±0.08/0.2, floor 1.3. See README "Known gaps" for why FSRS has not
 * been swapped in yet.
 */

import { DAY, MET, GRADUATE, LEARN, GAMES } from './constants.js';
import { stage, ELIG } from './stages.js';
import { state, save } from './state.js';
import { now, today, shuffle } from './text.js';

/* ---------- daily load ---------- */

/* Counts DISTINCT WORDS touched today, not raw answers. A 60-second Wahr
 * oder falsch sprint produces ~30 taps, so counting answers made the cap fire
 * within minutes and meant nothing. What burns people out is how many
 * different words they're juggling, which is also what Anki's limits measure.
 * Practice rounds are excluded entirely. */
export function todayCount() {
  return state.dayStamp === today() ? (state.dayWords || []).length : 0;
}
export function dailyCapReached() {
  return state.dailyCap > 0 && todayCount() >= state.dailyCap;
}
function bumpDayCount(cardId) {
  if (state.dayStamp !== today()) { state.dayStamp = today(); state.dayWords = []; }
  if (!state.dayWords) state.dayWords = [];
  if (cardId && !state.dayWords.includes(cardId)) state.dayWords.push(cardId);
}

/* ---------- retention ---------- */

/**
 * How well a word is currently held, 0..1, for the matrix and diagnostics.
 * Deliberately simple and explainable rather than a fitted forgetting curve,
 * since the scheduler underneath isn't FSRS either; presenting a precise
 * probability here would imply more rigour than the model has.
 */
export function retention(c) {
  if (!c.reps) return 0;
  if (c.lp < GRADUATE) return 0.15;                       // still in learning
  const skillAvg = (c.sk.rec + c.sk.gen + c.sk.pro + c.sk.app) / (4 * MET);
  const intervalPart = Math.min(1, Math.log10(1 + c.interval) / Math.log10(61)); // ~60d = full
  const lapsePenalty = Math.min(0.35, c.lapses * 0.05);
  const overdueDays = Math.max(0, (now() - c.due) / DAY);
  const overduePenalty = Math.min(0.30, overdueDays / 30);
  return Math.max(0, Math.min(1,
    0.55 * intervalPart + 0.45 * Math.min(1, skillAvg) - lapsePenalty - overduePenalty));
}

export const RETENTION_BANDS = [
  { key: "new",     label: "Not started", min: -1,    max: 0.0001 },
  { key: "fragile", label: "Fragile",     min: 0.0001, max: 0.30 },
  { key: "shaky",   label: "Shaky",       min: 0.30,  max: 0.55 },
  { key: "solid",   label: "Solid",       min: 0.55,  max: 0.80 },
  { key: "strong",  label: "Strong",      min: 0.80,  max: 1.01 },
];
export function bandOf(c) {
  const r = retention(c);
  return RETENTION_BANDS.find(b => r > b.min && r <= b.max) || RETENTION_BANDS[0];
}

/** How many reviews fall due on each of the next `days` days; index 0 = today. */
export function forecast(days = 14) {
  const out = Array.from({ length: days }, () => 0);
  const start = new Date(); start.setHours(0, 0, 0, 0);
  for (const c of state.cards) {
    if (c.suspended || !c.reps || c.lp < GRADUATE) continue;
    const d = Math.floor((c.due - start.getTime()) / DAY);
    if (d < 0) out[0]++;               // overdue piles onto today
    else if (d < days) out[d]++;
  }
  return out;
}

/** Overdue / due today / upcoming / learning / suspended, for the health panel. */
export function reviewBalance() {
  const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
  let overdue = 0, dueToday = 0, upcoming = 0, learning = 0, suspended = 0;
  for (const c of state.cards) {
    if (c.suspended) { suspended++; continue; }
    if (!c.reps) continue;
    if (c.lp < GRADUATE) { learning++; continue; }
    if (c.due < now() - DAY) overdue++;
    else if (c.due <= endOfToday.getTime()) dueToday++;
    else upcoming++;
  }
  return { overdue, dueToday, upcoming, learning, suspended };
}

/* ---------- grading ---------- */

/* Answering a word again before it's due must not compound its interval.
 * Without this, a few rounds in one sitting multiply the interval once per
 * answer and a word lands 12,000 days out. SM-2 (and Anki) only grow the
 * interval on a review that was actually due. */
const MAX_INTERVAL = 365;          // a hard ceiling, so no bug can ever outrun a year
const DAILY_SKILL_CAP = 1.5;       /* Must exceed the largest single weight (Tippen, 1.4) so one
                                      honest answer is never clipped, while staying under MET (2.5)
                                      so no skill can be cleared without a second day. */

/**
 * @param {object}  card
 * @param {boolean} ok
 * @param {string}  skill    rec | gen | pro | app
 * @param {string}  gameKey
 * @param {object}  [opts]
 * @param {number}  [opts.penalty=0]  0..0.9, from hints used on this question
 * @param {boolean} [opts.practice]   score only; never touch the schedule
 */
export function grade(card, ok, skill, gameKey, opts = {}) {
  const w = GAMES[gameKey].w;
  const penalty = Math.max(0, Math.min(0.9, opts.penalty || 0));

  /* Practice is a read-only drill: no credit, no interval change, no lapse.
     It exists so a folder can be hammered before a test without distorting
     the schedule built up over weeks. */
  if (opts.practice) {
    return { credit: 0, graduated: false, promoted: false, leeched: false, practice: true };
  }

  const before = stage(card);
  const wasDue = card.due <= now();
  card.seen = now(); state.reviews++;
  bumpDayCount(card.id);
  let credit = 0, graduated = false;

  if (ok) {
    /* skd[skill] records both the day and how much was earned on it, so
       repetition within one day can top out instead of running away. */
    const rec = normaliseSkd(card.skd[skill]);
    const sameDay = rec.d === today();
    const earnedToday = sameDay ? rec.e : 0;
    const room = Math.max(0, DAILY_SKILL_CAP - earnedToday);
    credit = Math.min(w * (sameDay ? 0.4 : 1) * (1 - penalty), room);
    card.sk[skill] = Math.min(5, card.sk[skill] + credit);
    card.skd[skill] = { d: today(), e: earnedToday + credit };
  } else {
    card.sk[skill] = Math.max(0, card.sk[skill] - 1);
  }
  card.reps++;
  if (!ok) card.lapses++;   // every miss counts, learning-phase included, so leeches are catchable

  if (card.lp < GRADUATE) {
    if (ok) {
      card.lp = Math.min(GRADUATE, card.lp + w * (1 - penalty));
      if (card.lp >= GRADUATE) { graduated = true; card.interval = 1; card.due = now() + DAY; }
      else card.due = now() + LEARN[Math.min(2, Math.floor(card.lp))];
    } else {
      card.lp = Math.max(0, card.lp - 1);
      card.due = now() + LEARN[0];
    }
  } else if (ok) {
    /* Only a genuinely due review earns interval growth. Answering early is
       still worth skill credit (above), but the schedule stays put. */
    if (wasDue) {
      const easy = w >= 1.0 && card.interval > 0 && penalty === 0;   // hinted answers never earn the bonus
      card.interval = Math.min(MAX_INTERVAL,
        Math.max(1, Math.round(card.interval * card.ease * (easy ? 1.25 : 1))));
      if (easy) card.ease = Math.min(2.9, card.ease + .08);
      card.due = now() + card.interval * DAY;
    }
  } else {
    card.ease = Math.max(1.3, card.ease - .2);
    card.lp = 1; card.interval = 0; card.due = now() + LEARN[0];
  }

  const after = stage(card);
  if (after > card.peak) card.peak = after;   // high-water mark: never falls, unlike the live stage
  let leeched = false;
  if (!ok && state.leechAt > 0 && card.lapses >= state.leechAt && !card.leech) {
    card.leech = true; card.suspended = true; leeched = true;
  }
  markDay(); save();
  return { credit, graduated, promoted: after > before, leeched };
}

/* Older saves stored skd[skill] as a bare day string. */
function normaliseSkd(v) {
  if (!v) return { d: null, e: 0 };
  if (typeof v === "string") return { d: v, e: 0 };
  return { d: v.d || null, e: v.e || 0 };
}

export function markDay() {
  const d = today();
  if (state.lastDay !== d) {
    const y = new Date(now() - DAY).toDateString();
    state.streak = (state.lastDay === y) ? state.streak + 1 : 1;
    state.lastDay = d;
  }
}

/**
 * Build a round.
 * @param {string}  key
 * @param {object}  [opts]
 * @param {string}  [opts.folder]    restrict to one folder
 * @param {boolean} [opts.practice]  ignore due dates entirely
 */
export function queue(key, opts = {}) {
  const g = GAMES[key];
  let pool = state.cards.filter(ELIG[key]).filter(c => !c.suspended);
  if (opts.folder) pool = pool.filter(c => c.folder === opts.folder);
  if (!pool.length) return [];

  /* Practice ignores scheduling: least-recently-seen first, so a repeated
     drill still rotates instead of showing the same handful every time. */
  if (opts.practice) {
    const byAge = pool.slice().sort((a, b) => a.seen - b.seen).slice(0, Math.max(g.n * 2, g.n));
    return shuffle(byAge).slice(0, g.n);
  }

  const due = shuffle(pool.filter(c => c.due <= now()));
  const onStage = shuffle(pool.filter(c => stage(c) === g.s && due.indexOf(c) < 0));
  const rest = pool.filter(c => due.indexOf(c) < 0 && onStage.indexOf(c) < 0)
                   .sort((a, b) => Math.abs(stage(a) - g.s) - Math.abs(stage(b) - g.s) || a.seen - b.seen);
  /* Due reviews matter, but they shouldn't crowd out a whole round and starve
     words still working toward this game's stage. Reserve half for each, and
     borrow from the other bucket only if one side comes up short. */
  const stageQuota = Math.max(1, Math.ceil(g.n / 2)), dueQuota = g.n - stageQuota;
  const out = due.slice(0, dueQuota).concat(onStage.slice(0, stageQuota));
  if (out.length < g.n) {
    const used = new Set(out.map(c => c.id));
    for (const c of due.concat(onStage)) { if (used.has(c.id)) continue; if (out.length >= g.n) break; out.push(c); used.add(c.id); }
    for (const c of rest) { if (used.has(c.id)) continue; if (out.length >= g.n) break; out.push(c); used.add(c.id); }
  }
  return shuffle(out);
}

export const playable = (key, folder) => state.cards
  .filter(ELIG[key])
  .filter(c => !c.suspended)
  .filter(c => !folder || c.folder === folder).length;

/* Human-readable next-review label. */
export function when(c) {
  if (c.lp < GRADUATE) return "learning";
  const d = c.due - now();
  return d <= 0 ? "due" : Math.max(1, Math.round(d / DAY)) + "d";
}
